// Preview implementation of the DataSource seam (task-017 scrape-preview funnel)
// and the fixture source the headless component tests boot against.
//
// It renders entirely from an in-memory payload — NO network. The task-017 flow
// scrapes a prospect's site, hands the extracted locations here, and the SAME
// <storadar-map> component renders them before any account or public_token
// exists. Because a freshly scraped dataset is a single brand's locations (tens,
// not the 100k the live clustering API is built for), preview is intentionally
// PIN-ONLY: it never synthesizes server-style clusters. It applies the viewport
// bbox filter and priority-then-distance ordering in-memory so the map/list sync
// behaves identically to live.

import { DataSource, DataSourceError } from "./DataSource";
import type {
  AutocompletePayload,
  Bbox,
  ConfigPayload,
  CustomField,
  LocationDetail,
  OpenNowState,
  TagCount,
  ViewportPayload,
  ViewportQuery,
  WidgetLocationRow,
} from "./types";
import { haversineMeters } from "../util/geo";

/**
 * A preview location: a [WidgetLocationRow] optionally enriched with the detail
 * fields the task-033 detail panel renders. [address]/[description] are OPTIONAL
 * here (Omit-ed from the required row shape) so the task-017 scraper and the test
 * fixtures can supply as little or as much as they have; absent fields become
 * nulls / empty arrays / "HOURS_UNKNOWN" in [loadDetail] and null on the directory
 * row. [tags] also feeds the viewport's per-tag facet counts.
 */
export interface PreviewLocation extends Omit<WidgetLocationRow, "address" | "description"> {
  tags?: string[];
  address?: string | null;
  city?: string | null;
  region?: string | null;
  postalCode?: string | null;
  countryCode?: string | null;
  phone?: string | null;
  email?: string | null;
  website?: string | null;
  description?: string | null;
  timezone?: string | null;
  openNow?: OpenNowState;
  hours?: Record<string, string[][]>;
  images?: string[];
  customFields?: CustomField[];
}

/** The self-contained payload a PreviewDataSource renders from. */
export interface PreviewPayload {
  config: ConfigPayload;
  locations: PreviewLocation[];
}

export class PreviewDataSource implements DataSource {
  constructor(private readonly payload: PreviewPayload) {}

  async loadConfig(): Promise<ConfigPayload> {
    return this.payload.config;
  }

  async loadViewport(query: ViewportQuery): Promise<ViewportPayload> {
    const pageSize = query.pageSize ?? 50;
    const inView = this.payload.locations.filter((pin) => withinBbox(pin, query.bbox));
    const ordered = orderLocations(inView, query);
    const start = query.page * pageSize;
    const pageLocations = ordered.slice(start, start + pageSize).map(toRow);
    return {
      // Preview is intentionally pin-only (a single brand's tens of locations, not
      // 100k) — the map never clusters, but the directory list is unchanged.
      mapMode: "PINS",
      clusters: null,
      locations: pageLocations,
      page: query.page,
      pageSize,
      hasMore: ordered.length > start + pageSize,
      scanCapReached: false,
      filters: {
        tagsApplied: query.tags,
        // Preview has no hours model, so open-now never runs and nothing is hidden.
        openNowApplied: false,
        timezoneUnknownExcluded: 0,
      },
      // Facet over EVERY in-view location's tags — independent of query.tags — so
      // the filter chips stay honestly toggleable (matches the server semantics).
      tagCounts: computeTagCounts(inView),
      // F-039: the extent of the COMPLETE ordered set (not the page), mirroring the server so
      // preview and live drive the camera identically — including its opt-in shape, so a preview
      // never returns bounds a live query would have withheld. Null when nothing matched too;
      // never a zeroed box.
      resultBounds: query.bounds && query.center ? boundsOf(ordered) : null,
      dataVersion: this.payload.config.dataVersion,
    };
  }

  /**
   * Local, no-network suggestions (task-032): filters the preview's own fixture
   * locations so a prospect can try search before an account or public_token
   * exists. Always [available] — there is no external provider to fail in
   * preview.
   *
   * Matches NAME OR ADDRESS. The live path sends the query to a geocoder, so a
   * visitor types a place ("Chicago"), not a shop name. Matching only the name
   * would leave the obvious search returning nothing while the live widget finds
   * the town — a preview that behaves worse than the thing it previews.
   */
  async loadAutocomplete(query: string): Promise<AutocompletePayload> {
    const needle = query.trim().toLowerCase();
    if (needle.length < 2) {
      return { query, suggestions: [], available: true };
    }
    const suggestions = this.payload.locations
      .filter((pin) => `${pin.name} ${pin.address ?? ""}`.toLowerCase().includes(needle))
      .slice(0, 6)
      .map((pin) => ({ label: pin.name, lat: pin.lat, lng: pin.lng }));
    return { query, suggestions, available: true };
  }

  /**
   * Builds a [LocationDetail] from the in-memory payload (the fixture seam the
   * tests drive). A missing id is a terminal (non-retryable) failure, mirroring
   * the live 404 — never a fabricated empty detail. Absent enrichment fields
   * degrade honestly to nulls / empty arrays / "HOURS_UNKNOWN".
   */
  async loadDetail(id: string): Promise<LocationDetail> {
    const loc = this.payload.locations.find((l) => l.id === id);
    if (!loc) {
      throw new DataSourceError("This location is no longer available.", false);
    }
    return {
      id: loc.id,
      name: loc.name,
      lat: loc.lat,
      lng: loc.lng,
      address: loc.address ?? null,
      city: loc.city ?? null,
      region: loc.region ?? null,
      postalCode: loc.postalCode ?? null,
      countryCode: loc.countryCode ?? null,
      phone: loc.phone ?? null,
      email: loc.email ?? null,
      website: loc.website ?? null,
      description: loc.description ?? null,
      timezone: loc.timezone ?? null,
      openNow: loc.openNow ?? "HOURS_UNKNOWN",
      hours: loc.hours ?? {},
      tags: loc.tags ?? [],
      images: loc.images ?? [],
      customFields: loc.customFields ?? [],
    };
  }
}

/**
 * The lat/lng extent of [locations], or null when there are none (F-039 camera fit). Honest
 * absence rather than a (0,0,0,0) box: null is what tells the component to leave the camera alone.
 */
function boundsOf(locations: PreviewLocation[]): Bbox | null {
  if (locations.length === 0) {
    return null;
  }
  const lats = locations.map((l) => l.lat);
  const lngs = locations.map((l) => l.lng);
  return {
    west: Math.min(...lngs),
    south: Math.min(...lats),
    east: Math.max(...lngs),
    north: Math.max(...lats),
  };
}

/** Counts locations per tag across the given set (the viewport facet, task-033). */
function computeTagCounts(locations: PreviewLocation[]): TagCount[] {
  const counts = new Map<string, number>();
  for (const loc of locations) {
    for (const tag of loc.tags ?? []) {
      counts.set(tag, (counts.get(tag) ?? 0) + 1);
    }
  }
  return [...counts.entries()].map(([tag, count]) => ({ tag, count }));
}

/** Longitude test tolerates an antimeridian-crossing bbox (west > east). */
function withinBbox(pin: { lat: number; lng: number }, bbox: Bbox): boolean {
  const latOk = pin.lat >= bbox.south && pin.lat <= bbox.north;
  const lngOk =
    bbox.west <= bbox.east
      ? pin.lng >= bbox.west && pin.lng <= bbox.east
      : pin.lng >= bbox.west || pin.lng <= bbox.east;
  return latOk && lngOk;
}

/**
 * Priority first (lower number = higher priority, nulls last), then by distance
 * to the search center when one was supplied — mirroring the server's F-111
 * ordering so the list looks the same in preview and live. Backfills
 * [PreviewLocation.distanceMeters] so the card renders "N km away" in preview too.
 */
function orderLocations(locations: PreviewLocation[], query: ViewportQuery): PreviewLocation[] {
  const withDistance = locations.map((loc) => ({
    loc,
    distance: query.center ? haversineMeters(query.center, loc) : null,
  }));
  withDistance.sort((a, b) => {
    const pa = a.loc.priority ?? Number.POSITIVE_INFINITY;
    const pb = b.loc.priority ?? Number.POSITIVE_INFINITY;
    if (pa !== pb) {
      return pa - pb;
    }
    if (a.distance != null && b.distance != null) {
      return a.distance - b.distance;
    }
    return 0;
  });
  return withDistance.map(({ loc, distance }) =>
    distance == null ? loc : { ...loc, distanceMeters: distance },
  );
}

/** Projects a preview location onto the wire directory row (optional fields → null). */
function toRow(loc: PreviewLocation): WidgetLocationRow {
  return {
    id: loc.id,
    name: loc.name,
    lat: loc.lat,
    lng: loc.lng,
    address: loc.address ?? null,
    description: loc.description ?? null,
    priority: loc.priority,
    distanceMeters: loc.distanceMeters,
  };
}
